import { Handle, Position, type NodeProps } from '@xyflow/react'
import { Select, Symbol } from '../../../ui'
import { useAutomationEditor } from '../editor-context'
import type { ConditionKeyType, ConditionNodeData } from '../types'

export function ConditionNode({ id, data }: NodeProps) {
  const nodeData = data as unknown as ConditionNodeData
  const key = nodeData.key ?? 'kind'
  const value = nodeData.value ?? 'chore'

  const {
    nodes,
    edges,
    connectingNodeId,
    startConnecting,
    cancelConnecting,
    connectNodes,
    disconnectEdge,
    removeNode,
  } = useAutomationEditor()

  const isConnectingFromHere = connectingNodeId === id
  const isCandidateTarget = Boolean(connectingNodeId && connectingNodeId !== id)

  // Ausgehende und eingehende Kanten
  const incomingEdges = edges.filter((e) => e.target === id)
  const outgoingEdges = edges.filter((e) => e.source === id)
  const connectedTargetIds = new Set(outgoingEdges.map((e) => e.target))

  const availableActionTargets = nodes
    .filter((n) => n.id !== id && !connectedTargetIds.has(n.id) && n.type === 'action')
    .map((n) => ({ id: n.id, label: 'DANN (Aktion)' }))

  const notifyChange = () => {
    window.dispatchEvent(new CustomEvent('node-data-change', { detail: { id } }))
  }

  const updateKey = (newKey: string) => {
    const typedKey = newKey as ConditionKeyType
    nodeData.key = typedKey
    if (typedKey === 'kind') nodeData.value = 'chore'
    else if (typedKey === 'hasRecipe') nodeData.value = true
    else nodeData.value = 'child'
    notifyChange()
  }

  const updateValue = (newVal: string) => {
    if (key === 'hasRecipe') {
      nodeData.value = newVal === 'true'
    } else {
      nodeData.value = newVal
    }
    notifyChange()
  }

  const handleDirectConnect = (targetId: string) => {
    if (targetId) {
      connectNodes(id, targetId)
    }
  }

  return (
    <div
      className={`editor-node editor-node--condition ${
        isConnectingFromHere
          ? 'editor-node--connecting-source'
          : isCandidateTarget
            ? 'editor-node--connecting-target'
            : ''
      }`}
      data-id={id}
    >
      <Handle
        id="cond-in"
        type="target"
        position={Position.Left}
        isConnectable={true}
        title="Eingang: Verbindung hierher ziehen oder anklicken"
        className="editor-handle editor-handle--target"
      />

      {isCandidateTarget && connectingNodeId && (
        <button
          type="button"
          className="editor-node__anheften-btn"
          onClick={() => connectNodes(connectingNodeId, id)}
          title="Hier anheften"
        >
          <Symbol name="automation" width={16} height={16} />
          Hier verbinden
        </button>
      )}

      <div className="editor-node__kopf">
        <span className="editor-node__symbol">
          <Symbol name="check" width={18} height={18} />
        </span>
        <span className="editor-node__kategorie">UND (Bedingung)</span>
        <button
          type="button"
          className="nodrag nopan editor-node__delete"
          onClick={(event) => {
            event.stopPropagation()
            removeNode(id)
          }}
          title="Bedingung entfernen"
          aria-label="Bedingung entfernen"
        >
          <Symbol name="trash" width={17} height={17} />
        </button>
      </div>

      <div className="editor-node__inhalt">
        <Select id={`cond-key-${id}`} label="Filter" wert={key} onWert={updateKey}>
          <option value="kind">Aufgabenart</option>
          <option value="hasRecipe">Rezept vorhanden</option>
          <option value="actorRole">Rolle des Akteurs</option>
        </Select>

        {key === 'kind' ? (
          <Select id={`cond-val-${id}`} label="Wert" wert={String(value)} onWert={updateValue}>
            <option value="chore">Nur Haushaltsaufgabe (Chore)</option>
            <option value="todo">Nur persönliche Aufgabe (Todo)</option>
          </Select>
        ) : key === 'hasRecipe' ? (
          <Select
            id={`cond-val-${id}`}
            label="Wert"
            wert={value ? 'true' : 'false'}
            onWert={updateValue}
          >
            <option value="true">Ja (Rezept verknüpft)</option>
            <option value="false">Nein</option>
          </Select>
        ) : (
          <Select id={`cond-val-${id}`} label="Wert" wert={String(value)} onWert={updateValue}>
            <option value="child">Kind</option>
            <option value="adult">Erwachsene(r)</option>
            <option value="owner">Haushaltsleitung</option>
          </Select>
        )}

        {/* Verknüpfungen */}
        <div className="editor-node__verbindungen">
          <div className="editor-node__verbindungen-kopf">
            <span className="editor-node__verbindungen-titel">
              {incomingEdges.length > 0 ? '✓ Verbunden' : 'Nicht verbunden'}
            </span>
            {!isConnectingFromHere ? (
              <button
                type="button"
                className="btn btn--tonal editor-node__connect-btn"
                onClick={() => startConnecting(id)}
                title="Klicken, um diesen Baustein mit einer Aktion zu verbinden"
              >
                + Verbinden
              </button>
            ) : (
              <button
                type="button"
                className="btn btn--text editor-node__connect-btn"
                onClick={cancelConnecting}
              >
                Abbrechen
              </button>
            )}
          </div>

          {outgoingEdges.length > 0 && (
            <div className="editor-node__chips">
              {outgoingEdges.map((e) => {
                const targetNode = nodes.find((n) => n.id === e.target)
                const targetLabel = targetNode?.type === 'action' ? 'Aktion' : e.target
                return (
                  <span key={e.id} className="editor-chip">
                    <span>→ {targetLabel}</span>
                    <button
                      type="button"
                      className="editor-chip__loeschen"
                      onClick={() => disconnectEdge(e.id)}
                      title="Verbindung trennen"
                      aria-label="Verbindung trennen"
                    >
                      ✕
                    </button>
                  </span>
                )
              })}
            </div>
          )}

          {availableActionTargets.length > 0 && (
            <div className="editor-node__schnell-wahl">
              <Select
                id={`condition-connect-${id}`}
                label="Verknüpfen"
                className="editor-node__mini-select"
                wert=""
                onWert={handleDirectConnect}
              >
                <option value="" disabled>
                  + Mit Aktion verknüpfen …
                </option>
                {availableActionTargets.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>
      </div>

      <Handle
        id="cond-out"
        type="source"
        position={Position.Right}
        isConnectable={true}
        title="Ausgang: Verbindung zum nächsten Schritt ziehen oder anklicken"
        className="editor-handle editor-handle--source"
      />
    </div>
  )
}
